import React, { useState } from 'react';
import { useUISettings } from '../../context/UISettingsContext';
import { useMessages } from '../../context/MessagesContext';
import encryption from '../../services/encryption';
import { Button } from '../common/Button';

export function PrivateKeyModal() {
  const { isPrivateKeyModalOpen, setIsPrivateKeyModalOpen, showNotification } = useUISettings();
  const { decryptAllMessages, messages, hasPrivateKey } = useMessages();
  const [tempPrivateKey, setTempPrivateKey] = useState('');
  const [isDecrypting, setIsDecrypting] = useState(false);

  if (!isPrivateKeyModalOpen) return null;
  
  // Simple validation for RSA private key format
  const isValidPrivateKey = (key) => {
    return key.includes('BEGIN RSA PRIVATE KEY') && key.includes('END RSA PRIVATE KEY');
  };
  
  const handleSubmit = async () => {
    if (!isValidPrivateKey(tempPrivateKey)) {
      showNotification('Invalid private key format. Please paste the full PEM key including BEGIN/END markers.', 'error');
      return;
    }
    
    setIsDecrypting(true);
    
    try {
      // Try to set the private key first
      encryption.setPrivateKey(tempPrivateKey);
      
      // Test if the key works by trying to decrypt a message (if any exist)
      const testMessage = messages.find(msg => msg.encryptedData && msg.recipientUuid === getUserUuid());
      
      if (testMessage) {
        try {
          // Test decrypt one message to verify the key works
          const testDecrypt = encryption.decryptMessage(testMessage, tempPrivateKey);
          if (!testDecrypt || testDecrypt === "[Decryption Failed]") {
            throw new Error("Key verification failed");
          }
          console.log("✅ Private key verified successfully");
        } catch (testError) {
          showNotification('The private key appears to be invalid or does not match this account.', 'error');
          setIsDecrypting(false);
          return;
        }
      }
      
      // Decrypt all messages
      const result = await decryptAllMessages(tempPrivateKey);
      
      if (result) {
        // Store the private key persistently
        const userUuid = getUserUuid();
        if (userUuid) {
          localStorage.setItem(`private_key_${userUuid}`, tempPrivateKey);
        }
        
        setTempPrivateKey('');
        setIsPrivateKeyModalOpen(false);
        showNotification('Private key loaded and messages decrypted successfully!', 'success');
      }
    } catch (error) {
      console.error('Decryption error:', error);
      showNotification('Failed to decrypt messages: ' + error.message, 'error');
    } finally {
      setIsDecrypting(false);
    }
  };
  
  const getUserUuid = () => {
    try {
      const session = localStorage.getItem('chat_session');
      if (session) {
        const parsed = JSON.parse(session);
        return parsed.uuid;
      }
    } catch (e) {
      console.error('Failed to get user UUID:', e);
    }
    return null;
  };
  
  const getMessageCount = () => {
    const userUuid = getUserUuid();
    const undecryptedCount = messages.filter(
      msg => !msg.decrypted && msg.recipientUuid === userUuid && msg.encryptedData
    ).length;
    return undecryptedCount;
  };
  
  const hasExistingKey = () => {
    return !!encryption.getPrivateKey();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Enter Private Key to Decrypt Messages</h3>
        
        <p className="text-sm text-gray-600 mb-2">
          Paste your full RSA private key below to decrypt all encrypted messages.
        </p>
        
        {!hasExistingKey() && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
            <p className="text-xs text-yellow-700">
              ⚠️ No private key is currently loaded. You won't be able to read any encrypted messages until you add it.
            </p>
          </div>
        )}
        
        {getMessageCount() > 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
            <p className="text-xs text-blue-700">
              ℹ️ You have {getMessageCount()} encrypted message(s) waiting to be decrypted.
            </p>
          </div>
        )}
        
        {getMessageCount() === 0 && hasExistingKey() && (
          <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4">
            <p className="text-xs text-green-700">
              ✓ All messages are already decrypted!
            </p>
          </div>
        )}
        
        <textarea 
          value={tempPrivateKey} 
          onChange={(e) => setTempPrivateKey(e.target.value)} 
          placeholder="-----BEGIN RSA PRIVATE KEY-----
MIIEpAIBAAKCAQEA...
-----END RSA PRIVATE KEY-----"
          rows="8" 
          className="w-full p-3 border border-gray-300 rounded-md mb-4 focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          autoFocus
          disabled={isDecrypting}
        />
        
        <div className="flex justify-end space-x-4">
          <Button 
            onClick={() => {
              setIsPrivateKeyModalOpen(false);
              setTempPrivateKey('');
            }} 
            variant="secondary"
            disabled={isDecrypting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isValidPrivateKey(tempPrivateKey) || isDecrypting}
            variant="primary"
          >
            {isDecrypting ? 'Decrypting...' : 'Decrypt All Messages'}
          </Button>
        </div>
      </div>
    </div>
  );
}